import React, { FunctionComponent, ChangeEvent, FocusEvent, useState, useEffect } from 'react';
import clsx from 'clsx';
import styles from './HourField.module.scss';
import { convertTo24Hours } from 'utils/DateUtils';

export interface HourFieldProps {
    id?: string;
    className?: string;
    value24Hs?: string;
    disabled?: boolean;
    error?: boolean;
    onFocus?: () => void;
    onChange: (value24Hs: string) => void;
}

const am = 'am';
const pm = 'pm';
const defaultHour = '8:00';

export const HourField: FunctionComponent<HourFieldProps> = ({
    id,
    className,
    value24Hs,
    disabled = false,
    error = false,
    onFocus,
    onChange
}) => {
    const parseHours = (value: string) => {
        const [hoursPart] = value.split(':');
        let hours = parseInt(hoursPart);
        if (hours === 0) {
            hours = 12;
        } else if (hours > 12) {
            hours = hours - 12;
        }
        return hours.toString();
    };

    const parseMinutes = (value: string) => {
        const [, minutes] = value.split(':');
        return minutes;
    };

    const parseAmPm = (value: string) => {
        const [hoursPart] = value.split(':');
        const hours = parseInt(hoursPart);
        return hours < 12 ? am : pm;
    };

    const [hours, setHours] = useState(value24Hs ? parseHours(value24Hs) : parseHours(defaultHour));
    const [minutes, setMinutes] = useState(value24Hs ? parseMinutes(value24Hs) : parseMinutes(defaultHour));
    const [amPm, setAmPm] = useState(value24Hs ? parseAmPm(value24Hs) : parseAmPm(defaultHour));

    useEffect(() => {
        setHours(value24Hs ? parseHours(value24Hs) : parseHours(defaultHour));
        setMinutes(value24Hs ? parseMinutes(value24Hs) : parseMinutes(defaultHour));
        setAmPm(value24Hs ? parseAmPm(value24Hs) : parseAmPm(defaultHour));
    }, [value24Hs]);

    const isEmpty = (value: string): boolean => {
        return value === '';
    };

    const isNumber = (value: string): boolean => {
        return !Number.isNaN(+value) && Number.isInteger(+value);
    };

    const isHour = (value: string): boolean => {
        return isNumber(value) && value.length <= 2 && +value >= 0 && +value <= 12;
    };

    const isMinutes = (value: string): boolean => {
        return isNumber(value) && value.length <= 2 && +value >= 0 && +value <= 59;
    };

    const formatHours = (value: string): string => {
        return value.length === 0 ? '0' : value;
    };

    const formatMinutes = (value: string): string => {
        if (value.length === 0) {
            return '00';
        } else if (value.length === 1) {
            return '0' + value;
        } else {
            return value;
        }
    };

    const changeHoursHandler = (event: ChangeEvent<HTMLInputElement>): void => {
        const newValue = event.currentTarget.value;
        if (isEmpty(newValue) || isHour(newValue)) {
            setHours(newValue);
        }
    };

    const changeMinutesHandler = (event: ChangeEvent<HTMLInputElement>): void => {
        const newValue = event.currentTarget.value;
        if (isEmpty(newValue) || isMinutes(newValue)) {
            setMinutes(newValue);
        }
    };

    const handleAmPmClick = (): void => {
        setAmPm(prev => {
            const newValue = prev === am ? pm : am;
            handleChange(hours, minutes, newValue);
            return newValue;
        });
    };

    const blurHoursHandler = (event: FocusEvent<HTMLInputElement>): void => {
        setHours(formatHours(hours));
        handleChange(formatHours(hours), minutes, amPm);
    };

    const blurMinutesHandler = (event: FocusEvent<HTMLInputElement>): void => {
        setMinutes(formatMinutes(minutes));
        handleChange(hours, minutes, amPm);
    };

    const handleChange = (hours: string, minutes: string, amPm: string): void => {
        onChange(convertTo24Hours(`${hours}:${minutes} ${amPm}`));
    };

    const containerClasses = clsx(
        styles.container,
        {
            [styles.disabled]: disabled,
            [styles.error]: error
        },
        className
    );

    const amPmButtonClasses = clsx(styles.amPmButton, {
        [styles.am]: amPm === am,
        [styles.pm]: amPm === pm
    });

    return (
        <div
            id={id}
            className={containerClasses}
        >
            <input
                type="text"
                value={hours}
                className={clsx(styles.input, styles.inputHours)}
                onChange={changeHoursHandler}
                onBlur={blurHoursHandler}
                onFocus={onFocus}
                disabled={disabled}
            />
            <div className={styles.separator}>
                <span className={styles.font}>:</span>
            </div>
            <input
                type="text"
                value={minutes}
                className={clsx(styles.input, styles.inputMinutes)}
                onChange={changeMinutesHandler}
                onBlur={blurMinutesHandler}
                onFocus={onFocus}
                disabled={disabled}
            />
            <button
                type="button"
                className={amPmButtonClasses}
                onClick={handleAmPmClick}
                disabled={disabled}
            >
                {amPm}
            </button>
        </div>
    );
};

export default HourField;
